/**
 * Copyright 2020 New Light Technologies, Inc.
 *
 * With Supporting Sponsorship from the Federal Emergency Management Agency (Contract: GSA Stars II GS-06F-0968Z)
 * In accordance with FAR 52.227-14(c)(iii), New Light Technologies, Inc. grants to the Government and others acting on its behalf a paid-up, nonexclusive, irrevocable, worldwide license in such copyrighted computer software and data to reproduce, prepare derivative works, and perform publicly and display publicly (but not to distribute copies to the public) by or on behalf of the Government.
 * Any other use, distribution, reproduction, modification, or publication without the prior express written authorization of New Light Technologies, Inc. is strictly prohibited.
 * All other rights are reserved by their respective copyright holders.
 *
 */
import type { Feature, FeatureCollection, GeoJsonProperties, Geometry, Polygon } from 'geojson';
import {
  countyBoundaryVariantAtom,
  countyChartDataAtom,
  fetchCountyBoundaries,
  getOutputStatisticsBySectionId,
  incidentChunksSelector,
  useSetCountyBoundaries,
  type CountyChartDatum,
} from './atoms';
import { FlyToInterpolator, type PickingInfo } from '@deck.gl/core';
import { PolygonLayer, TextLayer } from '@deck.gl/layers';
import { selectedFeatureAtom } from './FeaturePopup';
import { useAtomValue, useAtom, useSetAtom, atom } from 'jotai';
import { viewStateAtom } from '../map/atoms';
import {
  COUNTY_GEOMETRY_LADDER,
  useGeometryDetail,
  type GeometryDetailRequest,
} from '../map/geometryDetail';
import React from 'react';
import { useMatch } from 'react-router-dom';
import { PathStyleExtension, type PathStyleExtensionProps } from '@deck.gl/extensions';
import { centerOfMass } from '@turf/turf';
import { getPercentColor } from '../Post/PostLayer';
import { Legend } from '../components/Legend';
import type { RGBAColor } from '../map/deckgl-types';
import { generateCountyChartTitle } from './IncidentCountyChart';
import { layerVisibility } from '../map/atoms';
import { isDarkBasemap, mapStyleAtom } from '../map/basemaps';

type Props = {
  autoHighlight: boolean;
};

/**
 * A county feature with MultiPolygon parts expanded to individual Polygons.
 *
 * The row itself stays open: fetchCountyBoundaries aliases the reference table's
 * columns to county_name/id, and the statistic column is chosen at runtime.
 */
type CountyPolygonFeature = Feature<Polygon, GeoJsonProperties>;

/** A county feature carrying the chart row plotted for it, for the label layer. */
type CountyLabelFeature = Feature<Geometry, Record<string, unknown>>;

export const viewCountiesAtom = atom<boolean>(false);

/**
 * Waits before each retry of a failed county boundary load, and by its length the
 * retry count.
 *
 * Needed because nothing else re-runs the load: the resolution stays unrecorded on
 * failure, so neither it nor the wanted resolution changes and the effect is not
 * re-evaluated. Without this a stationary user who hit one 502 would have no
 * boundaries, an inert toggle and no county highlighting for the session.
 *
 * Bounded and backed off because the endpoint may be down rather than flaky, and
 * every open tab is a client: 4 requests over 20 seconds per resolution, then
 * silence, versus a poll that would keep a struggling API busy for as long as the
 * tab is open. A camera change into a band whose resolution never loaded still
 * starts a fresh sequence, so recovery is not limited to these 20 seconds.
 */
export const BOUNDARY_RETRY_DELAYS_MS = [1000, 4000, 15000];

/**
 * County boundaries at the finer resolution the camera needs, for the viewport
 * only, or null while a whole-table resolution is faithful on its own.
 *
 * The finer columns exist because the whole country at them (25 MB and 61 MB of
 * GeoJSON, 296 MB at full resolution) is past what the API will serve in one
 * response. A superseded request is aborted, which cancels the server's query
 * too, and the boundaries already drawn stay until the replacement resolves.
 */
function useCountyBoundaryDetail(request: GeometryDetailRequest | null): FeatureCollection | null {
  const [detail, setDetail] = React.useState<FeatureCollection | null>(null);

  React.useEffect(() => {
    if (request == null) return;

    const controller = new AbortController();
    fetchCountyBoundaries(request.variant, request.bbox, controller.signal)
      .then(collection => {
        if (!controller.signal.aborted) setDetail(collection);
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        // The whole-table boundaries stay on the map, so a failure costs
        // fidelity rather than the outlines themselves.
        console.error('Failed to load viewport county boundaries:', error);
      });

    return () => {
      controller.abort();
    };
  }, [request]);

  // Without a request there is no viewport to refine, so the last response is
  // declined here rather than cleared from the effect -- clearing it would cost
  // a second render pass for a value this hook can simply not return.
  return request == null ? null : detail;
}

export const useCountyLayer = (props: Props) => {
  const mapStyle = useAtomValue(mapStyleAtom);

  const [selectedFeature, setSelectedFeature] = useAtom(selectedFeatureAtom);
  const setViewState = useSetAtom(viewStateAtom);
  const viewCountiesBoundary = useAtomValue(viewCountiesAtom);
  const hightlightCounties = useAtomValue(layerVisibility('county-highlights'));

  const {
    params: { incidentId, sectionId },
  } = useMatch({ path: '/:incidentId/:sectionId/:viewType/:attributeId?' }) ?? {
    params: {},
  };

  const data = useAtomValue(
    incidentChunksSelector({
      incidentId: 'county',
      sectionId: 'county',
      visible: true,
    })
  );

  const countyIncidentStatsData = useAtomValue(countyChartDataAtom({ incidentId, sectionId }));

  const wholeCountyData = React.useMemo(() => data.flatMap(a => a.data), [data]);

  // The coarsest resolution whose generalization is still invisible at this
  // camera: a whole-table column while one of them is faithful, and above that
  // the whole-table set as a backdrop plus finer geometry for the viewport.
  const { base: countyVariant, detail: countyDetailRequest } =
    useGeometryDetail(COUNTY_GEOMETRY_LADDER);
  const countyDetail = useCountyBoundaryDetail(countyDetailRequest);
  const loadedVariant = useAtomValue(countyBoundaryVariantAtom);

  // Counties the viewport set accounts for are dropped from the whole-table set
  // rather than drawn underneath it: the two are the same rows at different
  // resolutions, so both would draw two outlines and stack two labels per county.
  // objectid is the same value in both, the table being static reference data.
  // Each chunk carries the layer id it is drawn as, so the viewport set keeps its
  // own layer however many whole-table chunks are left.
  const countyChunks = React.useMemo(() => {
    const wholeChunks = wholeCountyData.map((chunk, index) => ({
      id: `county-chunk-${index}`,
      features: chunk.features,
    }));
    if (countyDetail == null) {
      return wholeChunks;
    }
    const detailed = new Set(countyDetail.features.map(feature => feature.properties?.id));
    return [
      ...wholeChunks
        .map(chunk => ({
          ...chunk,
          features: chunk.features.filter(feature => !detailed.has(feature.properties?.id)),
        }))
        .filter(chunk => chunk.features.length > 0),
      { id: 'county-viewport-chunk', features: countyDetail.features },
    ];
  }, [wholeCountyData, countyDetail]);
  const setCountyBoundaries = useSetCountyBoundaries();

  React.useEffect(() => {
    // A null variant means the camera has not settled yet: fetching now would
    // buy the national view for a route that is still flying to an incident.
    if (countyVariant == null || loadedVariant === countyVariant) {
      return;
    }

    // A controller rather than a boolean, so abandoning reaches the load itself:
    // a superseded whole-table load that SUCCEEDS must not write its collection
    // or mark its resolution as the loaded one either.
    const controller = new AbortController();
    let retryTimer: ReturnType<typeof setTimeout> | undefined;

    const load = (retriesUsed: number): void => {
      setCountyBoundaries(countyVariant, controller.signal).catch((error: unknown) => {
        if (controller.signal.aborted) {
          return;
        }
        // Whatever resolution is already on the map keeps rendering, and the
        // failed one stays unrecorded, so this effect owns getting it loaded.
        console.error('Failed to load county boundaries:', error);
        const delay = BOUNDARY_RETRY_DELAYS_MS[retriesUsed];
        if (delay == null) {
          return;
        }
        retryTimer = setTimeout(() => load(retriesUsed + 1), delay);
      });
    };
    load(0);

    return () => {
      // A camera change to another band, or an unmount, abandons the sequence:
      // the boundaries this was loading are no longer the ones to draw.
      controller.abort();
      clearTimeout(retryTimer);
    };
  }, [countyVariant, loadedVariant, setCountyBoundaries]);

  const countyFieldName = getOutputStatisticsBySectionId(sectionId ?? 'population').find(
    fields => fields.onStatisticField === countyIncidentStatsData?.yAttribute
  )?.countyFieldName;

  /**
   * The plotted rows keyed by the county each one was drawn against.
   *
   * The chart publishes its axis labels and its rows as two arrays, and they are
   * not one-to-one: `labels` is the deduped county axis, while `data` is the rows
   * of the single dataset being drawn -- in POST-rank mode only the rank-1 rows,
   * which is a subset of the axis and can be empty. Pairing them by position read
   * past the end of `data` and dereferenced undefined, so the row is found by the
   * county name it carries instead. First occurrence wins, matching the position
   * the old `labels.indexOf` lookup resolved to when a county has several rows.
   */
  const statsByCounty = React.useMemo(() => {
    const byCounty = new Map<string, CountyChartDatum>();
    for (const datum of countyIncidentStatsData?.data ?? []) {
      if (typeof datum.x === 'string' && !byCounty.has(datum.x)) {
        byCounty.set(datum.x, datum);
      }
    }
    return byCounty;
  }, [countyIncidentStatsData]);

  const handleFillColor = React.useCallback(
    (d: CountyPolygonFeature): RGBAColor => {
      const countyStats = statsByCounty.get(d.properties?.county_name);
      if (hightlightCounties && countyIncidentStatsData != null && countyStats != null) {
        // The statistic arrives as whatever JSON the aggregation produced -- a sum
        // over a bigint column comes back as a numeric string -- and everything
        // below it is arithmetic.
        const value = Number(countyStats[countyIncidentStatsData.yAttribute]);

        if (countyIncidentStatsData.chartDataType === 'percent' && countyFieldName) {
          const countyTotal = Number(d.properties?.[countyFieldName]);
          const percent = value / countyTotal;
          const [red, green, blue] = getPercentColor(percent * 100);
          return [red, green, blue, 120];
        }

        const percentRank = () => {
          const sorted = countyIncidentStatsData.data
            .map(row => Number(row[countyIncidentStatsData.yAttribute]))
            .sort((a, b) => a - b);
          const rank = sorted.indexOf(value);
          return rank / sorted.length;
        };

        const [red, green, blue] = getPercentColor(percentRank() * 100);
        return [red, green, blue, 120];
      }
      return d.properties?.id === selectedFeature?.properties?.id
        ? [0, 206, 265, 50]
        : [0, 0, 0, 0];
    },
    [
      countyFieldName,
      selectedFeature?.properties?.id,
      countyIncidentStatsData,
      statsByCounty,
      hightlightCounties,
    ]
  );

  const handleClick = React.useCallback(
    (info: PickingInfo<CountyPolygonFeature>) => {
      if (info.object == null) {
        return;
      }
      setSelectedFeature({ ...info.object, section: 'county' });
      // deck.gl resolves a coordinate for every pick over the map; without one
      // there is nowhere to fly.
      if (info.coordinate != null) {
        const [longitude, latitude] = info.coordinate;
        setViewState(view => ({
          ...view,
          longitude,
          latitude,
          transitionInterpolator: new FlyToInterpolator({ speed: 3.0 }),
          transitionDuration: 1000,
        }));
      }
    },
    [setSelectedFeature, setViewState]
  );

  const textData = React.useMemo(() => {
    return countyChunks
      .flatMap(chunk => chunk.features)
      .filter(d => countyIncidentStatsData?.labels.includes(d.properties?.county_name))
      .map((f): CountyLabelFeature => ({
        ...f,
        properties: {
          ...f.properties,
          // Keyed by county for the same reason as the fill colour: the label
          // position is not a position in the plotted rows, so indexing by it
          // attached another county's statistics as often as none at all.
          incidentStats: statsByCounty.get(f?.properties?.county_name),
        },
      }));
  }, [countyChunks, countyIncidentStatsData, statsByCounty]);

  function removeStateFromCountyName(countyName: string) {
    const countyNameParts = countyName.split(',');
    if (countyNameParts.length > 1) {
      return countyNameParts.slice(0, -1).join(' ').split(' ').slice(0, -1).join(' ');
    }
    return countyName;
  }

  const textLayer = React.useMemo(() => {
    return new TextLayer<CountyLabelFeature>({
      id: 'county-text-layer',
      data: textData,
      getColor: () => (isDarkBasemap(mapStyle) ? [255, 255, 255] : [0, 62, 103]),
      pickable: false,
      getPosition: d => {
        const post = centerOfMass(d).geometry.coordinates;
        return [post[0], post[1]];
      },
      // Every datum passed the labels filter above, so its county_name is one of
      // the chart's label strings.
      getText: d => removeStateFromCountyName(String(d.properties.county_name)),
      fontFamily:
        '"ITC Franklin Gothic", "Source Sans Pro", Open Sans, Arial, Helvetica, sans-serif',
      background: true,
      getBackgroundColor: [255, 255, 255, 20],
      backgroundPadding: [4, 4],
      billboard: false,
      getSize: 4000,
      fontSettings: {
        sdf: true,
      },
      outlineColor: [0, 62, 103, 50],
      outlineWidth: 0.3,
      sizeScale: 2,
      sizeUnits: 'meters',
      getAngle: 0,
      getTextAnchor: 'middle',
      getAlignmentBaseline: 'center',
      visible: hightlightCounties,
      updateTriggers: {
        getText: [countyIncidentStatsData],
        getColor: [mapStyle],
      },
    });
  }, [countyIncidentStatsData, textData, hightlightCounties, mapStyle]);

  const layers = React.useMemo(() => {
    return countyChunks.map(chunk => {
      // 329 of the 3,144 counties are MultiPolygons (island/coastal parts);
      // PolygonLayer draws one polygon per datum, so expand each part into its
      // own entry or everything after the first part silently disappears.
      const polygonFeatures = chunk.features.flatMap(feature =>
        feature.geometry.type === 'MultiPolygon'
          ? feature.geometry.coordinates.map(coordinates => ({
              ...feature,
              geometry: { type: 'Polygon' as const, coordinates },
            }))
          : [feature]
      );
      return new PolygonLayer<CountyPolygonFeature, PathStyleExtensionProps>({
        id: chunk.id,
        data: polygonFeatures,
        getPolygon: d => d.geometry.coordinates,
        getFillColor: handleFillColor,
        getLineColor: isDarkBasemap(mapStyle) ? [170, 190, 240] : [100, 120, 185],
        autoHighlight: props.autoHighlight,
        highlightColor: [0, 206, 265, 50],
        getLineWidth: 2,
        lineWidthMinPixels: 1,
        pickable: viewCountiesBoundary,
        filled: true,
        wireframe: false,
        stroked: viewCountiesBoundary,
        material: false,
        visible: true,
        getDashArray: [20, 10],
        dashJustified: true,
        lineWidthScale: 2,
        extensions: [new PathStyleExtension({ dash: true })],
        updateTriggers: {
          getFillColor: [selectedFeature, handleFillColor],
        },

        onClick: handleClick,
      });
    });
  }, [
    countyChunks,
    handleFillColor,
    handleClick,
    props.autoHighlight,
    selectedFeature,
    viewCountiesBoundary,
    mapStyle,
  ]);

  return [layers, textLayer];
};

const colorRange: RGBAColor[] = [
  [181, 9, 9, 190], // #B50909
  [219, 129, 0, 190], // #DB8100
  [226, 178, 0, 190], // #E2B200
  [143, 195, 201, 190], // #8FC3C9
  [95, 133, 188, 190], // #5F85BC
];

const percentlegendItems = [
  { label: '60% - 100%', color: `rgb(${colorRange[0]})` },
  { label: '45% - 60%', color: `rgb(${colorRange[1]})` },
  { label: '30% - 45%', color: `rgb(${colorRange[2]})` },
  { label: '15% - 30%', color: `rgb(${colorRange[3]})` },
  { label: '0% - 15%', color: `rgb(${colorRange[4]})` },
];

export const CountyLegend = () => {
  const {
    params: { incidentId, sectionId },
  } = useMatch({ path: '/:incidentId/:sectionId/:viewType/:attributeId?' }) ?? {
    params: {},
  };

  const countyIncidentStatsData = useAtomValue(countyChartDataAtom({ incidentId, sectionId }));

  function findPercentileRanges(data: number[], percentiles: number[]): number[] {
    // No values means no percentiles: indexing an empty array would hand back a
    // number[] whose entries are all undefined.
    if (data.length === 0) {
      return [];
    }
    const sortedData = [...data].sort((a, b) => a - b);
    return percentiles.map(percentile => {
      const index = Math.floor((sortedData.length - 1) * (percentile / 100));
      return sortedData[index];
    });
  }

  const rangeValues = findPercentileRanges(
    countyIncidentStatsData?.data.map(row => Number(row[countyIncidentStatsData.yAttribute])) ?? [],
    [100, 60, 45, 30, 15]
  );

  const sumLegendItems = rangeValues
    ? rangeValues.filter(Boolean).map((value, index) => ({
        label: `${((rangeValues?.[index + 1] ?? 0) + 1)?.toLocaleString?.('en-US', {
          maximumSignificantDigits: 3,
          maximumFractionDigits: 2,
          compactDisplay: 'short',
          notation: 'compact',
        })} - ${value?.toLocaleString?.('en-US', {
          maximumSignificantDigits: 3,
          maximumFractionDigits: 2,
          compactDisplay: 'short',
          notation: 'compact',
        })} `,
        color: `rgb(${colorRange[index][0]}, ${colorRange[index][1]}, ${colorRange[index][2]})`,
      }))
    : [];

  return (
    <Legend
      id="county-highlights"
      title={
        generateCountyChartTitle(
          sectionId ?? 'population',
          countyIncidentStatsData?.yAttribute ?? 'ls_night_pop',
          countyIncidentStatsData?.chartDataType ?? 'sum'
        ) + ' by County'
      }
      items={countyIncidentStatsData?.chartDataType === 'sum' ? sumLegendItems : percentlegendItems}
    />
  );
};
